// src/features/beitragsrechnung/services/beitragsrechnungService.ts
//
// Zugriff auf die Beitragsrechnung über die Netlify-Funktion beitragsrechnung.
// Als Ausweis dient die persönliche Anmeldung (App-Token aus dem Google-Login in
// localStorage 'authToken'), nicht das gemeinsame Datenbankkonto. Gleiches
// Muster wie der Posteingang.

import { netlifyFetch } from '../../../shared/services/netlifyFetch';
import { BeitragsrechnungBuchung, BeitragsrechnungDaten, DetailKategorie, Tilgung } from '../types';

export class BeitragsrechnungFehler extends Error {
  neuAnmelden: boolean;

  constructor(message: string, neuAnmelden = false) {
    super(message);
    this.name = 'BeitragsrechnungFehler';
    this.neuAnmelden = neuAnmelden;
  }
}

class BeitragsrechnungService {
  private async aufrufen(nutzlast: Record<string, unknown>): Promise<any> {
    const identitaet = localStorage.getItem('authToken');
    if (!identitaet) {
      throw new BeitragsrechnungFehler('Für diese Seite ist die persönliche Anmeldung im Intranet erforderlich. Bitte neu anmelden.', true);
    }

    const res = await netlifyFetch('/.netlify/functions/beitragsrechnung', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${identitaet}` },
      body: JSON.stringify(nutzlast)
    });
    const daten = await res.json().catch(() => ({}));

    if (res.status === 401) {
      throw new BeitragsrechnungFehler('Deine Anmeldung ist abgelaufen. Bitte melde dich neu an.', true);
    }
    if (!res.ok || !daten.success) {
      throw new BeitragsrechnungFehler(daten.error || `HTTP ${res.status}`);
    }
    return daten;
  }

  async ladeJahr(jahr: number): Promise<BeitragsrechnungDaten> {
    const daten = await this.aufrufen({ aktion: 'daten', jahr });
    return {
      jahr: daten.jahr,
      monate: daten.monate || [],
      einstellungen: daten.einstellungen,
      tilgungen: daten.tilgungen || []
    };
  }

  async ladeBuchungen(jahr: number, von: number, bis: number, kategorie: DetailKategorie): Promise<BeitragsrechnungBuchung[]> {
    const daten = await this.aufrufen({ aktion: 'buchungen', jahr, von, bis, kategorie });
    return (daten.buchungen || []).map((b: BeitragsrechnungBuchung) => ({ ...b, betrag: Number(b.betrag) }));
  }

  async speichereTilgung(jahr: number, monat: number, betrag: number, notiz: string): Promise<Tilgung> {
    const daten = await this.aufrufen({ aktion: 'tilgung_speichern', jahr, monat, betrag, notiz });
    return daten.tilgung as Tilgung;
  }

  async loescheTilgung(id: string): Promise<void> {
    await this.aufrufen({ aktion: 'tilgung_loeschen', id });
  }
}

// Singleton Instance
export const beitragsrechnungService = new BeitragsrechnungService();
